import { arrayNotationToObject, type NestedRecordStringString } from "@/utils/arrayNotationToObject"
import { getVfFormInject, type VfFormInject } from "@/vf/utils/SymfonyForm"
import _ from "lodash"
import { computed, type Ref } from "vue"

export function appendFormDataToFormData(appendTo: FormData, append: FormData, asKey: string) {
    append.forEach((value: FormDataEntryValue, key: string) => {
        const keys: string[] = key.replace("]", "").split("[")
        appendTo.append(asKey + "[" + keys.join("][") + "]", value)
    })
    return appendTo
}

export function formDataToObject(formData: FormData): NestedRecordStringString {
    const dataObject: Record<string, string> = {}
    formData.forEach((value, key) => {
        dataObject[key] = value as string
    })

    return arrayNotationToObject(dataObject)
}

//gets the path of a form element from the schema (e.g. "steps.step1.translations")
export function getFormPath(name: string) {
    const form = getVfFormInject()
    const path: string[] = []

    //finds a key-value-pair ("name": name) in an object and adds breadcrumb to path array (e.g. ['[0]', 'children[0]', 'children[0]'])
    function findPathInObject(obj: object | string[]): string | boolean {
        if (!obj || (typeof obj !== "object" && !Array.isArray(obj))) {
            return false
        } else if ("name" in obj && obj["name"] === name) {
            return true
        } else if (Array.isArray(obj)) {
            const parentKey = path.length ? path.pop() : ""

            for (let i = 0; i < obj.length; i++) {
                path.push(`${parentKey}[${i}]`)
                const result = findPathInObject(obj[i])
                if (result) {
                    return result
                }
                path.pop()
            }
        } else {
            for (const [key, value] of Object.entries(obj)) {
                path.push(key)
                const result = findPathInObject(value)
                if (result) {
                    return result
                }
                path.pop()
            }
        }

        return false
    }

    findPathInObject(form?.schema._rawValue)

    //gets name value for each breadcrumb in path array (e.g. ['steps', 'step1', 'translations'])
    const namedPaths = [name]

    if (path.length > 2) {
        for (let i = 0; i <= path.length; i++) {
            path.pop()
            namedPaths.unshift(_.get(form?.schema._rawValue, path.join(".") + ".name"))
        }
    }

    return namedPaths.join(".")
}

/**
 * Reactive replacement for VF SymfonyForm's `findDataForName`.
 * Returns the parent data object for the given field name as a computed ref,
 * so consumers react to changes in the form model.
 */
export function findDataForNameReactive(name: string, elementName: string, form?: VfFormInject): Ref<any> {
    if (!form) {
        form = getVfFormInject()
    }
    if (!form) {
        throw Error(elementName + ": no VfForm found")
    }
    const nameParts = name.split(".")
    nameParts.pop()
    return computed(() => {
        let data = form!.formModel.value
        for (const namePart of nameParts) {
            data = data[namePart]
        }
        return data
    })
}

// VfForm converts entity arrays to IDs only for the input types it knows, so the value of a custom multiple entity
// input may still contain the serialized entities
export function toEntityIds(value: unknown): string[] {
    if (!Array.isArray(value)) {
        return []
    }
    return value.map(v => (typeof v === "object" && v !== null && "id" in v ? String(v.id) : String(v)))
}
